/**
 * Unified in-app search (Ctrl/Cmd+F) across the source editor and the
 * rendered preview.
 *
 * The browser's native find is a poor fit for the multi-panel layout: it
 * can't see into CodeMirror's virtualized document, walks hidden panels,
 * and gives no per-pane control. This module replaces it with a single
 * floating find bar that targets one pane ("scope") at a time:
 *
 *  - **Source**: matches are computed over the full CodeMirror document
 *    (not just the rendered viewport) and shown via mark decorations.
 *  - **Preview**: text nodes of #markdown-output are indexed into a
 *    whitespace-normalized haystack so queries match across inline element
 *    boundaries (e.g. `**bold** text`) and across soft line wraps in the
 *    HTML. Highlights use the CSS Custom Highlight API, which keeps the
 *    preview DOM untouched (no <mark> wrapping that could confuse
 *    enrichers or checklists).
 *
 * Scope is chosen from focus/visibility on open and can be switched from
 * the bar. Navigation wraps around; the current match is auto-scrolled
 * into view (expanding collapsed code blocks when needed).
 */
import { EditorView, Decoration, keymap } from '@codemirror/view';
import type { DecorationSet } from '@codemirror/view';
import { StateField, StateEffect, Prec, RangeSetBuilder } from '@codemirror/state';
import type { Extension } from '@codemirror/state';

export type SearchScope = 'source' | 'preview';

interface EditorMatch {
  from: number;
  to: number;
}

interface EditorMatchSpec {
  ranges: EditorMatch[];
  current: number;
}

/** Where a haystack character came from in the preview DOM. */
interface CharPosition {
  node: globalThis.Text;
  offset: number;
}

const MAX_MATCHES = 2000;
const HIGHLIGHT_ALL = 'leedoc-search';
const HIGHLIGHT_CURRENT = 'leedoc-search-current';

// ---------------------------------------------------------------------------
// CodeMirror highlight field (module-level so every EditorState shares it)
// ---------------------------------------------------------------------------

const setEditorMatches = StateEffect.define<EditorMatchSpec | null>();

const matchMark = Decoration.mark({ class: 'cm-ld-search-match' });
const currentMark = Decoration.mark({ class: 'cm-ld-search-match cm-ld-search-current' });

const searchHighlightField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(deco, tr) {
    // Map through edits so highlights track the text until the next recompute
    deco = deco.map(tr.changes);
    for (const effect of tr.effects) {
      if (effect.is(setEditorMatches)) {
        const spec = effect.value;
        if (!spec || spec.ranges.length === 0) {
          deco = Decoration.none;
          continue;
        }
        const builder = new RangeSetBuilder<Decoration>();
        spec.ranges.forEach((r, i) => {
          if (r.to > r.from) builder.add(r.from, r.to, i === spec.current ? currentMark : matchMark);
        });
        deco = builder.finish();
      }
    }
    return deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** CSS Custom Highlight API, feature-detected (typed loosely for older lib.dom). */
function highlightRegistry(): Map<string, unknown> | null {
  const css = (globalThis as any).CSS;
  const HighlightCtor = (globalThis as any).Highlight;
  if (!css || !css.highlights || typeof HighlightCtor !== 'function') return null;
  return css.highlights as Map<string, unknown>;
}

// Elements whose text is UI chrome, not document content
const CHROME_SELECTOR = '.code-copy-btn, .code-collapse-toggle, .code-collapse-badge, .line-numbers';

// Block-level containers: a match may span inline elements but never cross
// one of these boundaries.
const BLOCK_TAGS = new Set([
  'P', 'DIV', 'LI', 'UL', 'OL', 'TD', 'TH', 'TR', 'TABLE', 'PRE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'SECTION',
  'ARTICLE', 'HEADER', 'FOOTER', 'NAV', 'DETAILS', 'SUMMARY',
  'FIGURE', 'FIGCAPTION',
]);

export interface PaneSearchOptions {
  getEditorView: () => EditorView | null;
  /** #editor-panel — hosts the bar when scope is 'source' */
  sourcePanel: HTMLElement;
  /** .markdown-panel — hosts the bar when scope is 'preview' */
  previewPanel: HTMLElement;
  /** #markdown-output — searched content AND the scroll container */
  previewContainer: HTMLElement;
}

export class PaneSearch {
  private readonly opts: PaneSearchOptions;

  private bar!: HTMLDivElement;
  private input!: HTMLInputElement;
  private countEl!: HTMLSpanElement;
  private caseBtn!: HTMLButtonElement;
  private prevBtn!: HTMLButtonElement;
  private nextBtn!: HTMLButtonElement;
  private scopeGroup!: HTMLDivElement;
  private scopeButtons: Record<SearchScope, HTMLButtonElement> = {} as Record<SearchScope, HTMLButtonElement>;

  private openState = false;
  private scope: SearchScope = 'preview';
  private caseSensitive = false;
  private currentIndex = 0;
  private capped = false;

  private editorMatches: EditorMatch[] = [];
  private previewRanges: Range[] = [];

  private refreshTimer: number | null = null;

  constructor(opts: PaneSearchOptions) {
    this.opts = opts;
    this.buildBar();

    // Recompute when the preview re-renders (variable edits, auto-refresh,
    // enricher passes). Highlight API highlights don't mutate the DOM, so
    // this never observes its own work.
    const observer = new MutationObserver(() => {
      if (this.openState && this.scope === 'preview') this.scheduleRefresh();
    });
    observer.observe(opts.previewContainer, { childList: true, subtree: true, characterData: true });
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  isOpen(): boolean {
    return this.openState;
  }

  /**
   * Open the find bar. Scope resolution: explicit argument → pane that owns
   * focus → source if the editor is open, otherwise preview.
   *
   * `initial` prefills the query (and case mode) instead of the pane's
   * selection — used by project-wide search to continue a search in-file.
   */
  open(preferred?: SearchScope, initial?: { query: string; caseSensitive?: boolean }): void {
    const sourceAvail = this.isPaneVisible('source');
    const previewAvail = this.isPaneVisible('preview');
    if (!sourceAvail && !previewAvail) return;

    let scope = preferred;
    if (scope === 'source' && !sourceAvail) scope = 'preview';
    if (scope === 'preview' && !previewAvail) scope = 'source';
    if (!scope) {
      const active = document.activeElement;
      if (sourceAvail && active && this.opts.sourcePanel.contains(active)) scope = 'source';
      else if (previewAvail && active && this.opts.previewPanel.contains(active)) scope = 'preview';
      else scope = sourceAvail ? 'source' : 'preview';
    }

    if (this.openState && scope !== this.scope) this.clearHighlights(this.scope);
    this.scope = scope;
    this.openState = true;

    this.attachBar();
    this.updateScopeUI();
    this.bar.classList.remove('hidden');

    if (initial) {
      this.input.value = initial.query;
      if (initial.caseSensitive !== undefined) {
        this.caseSensitive = initial.caseSensitive;
        this.caseBtn.classList.toggle('active', this.caseSensitive);
      }
    } else {
      // Prefill from the pane's current selection (like editors do)
      const selected = this.selectedTextInScope();
      if (selected) this.input.value = selected;
    }
    this.input.focus();
    this.input.select();

    this.runSearch({ resetCurrent: true, scroll: true });
  }

  close(): void {
    if (!this.openState) return;
    this.openState = false;
    this.bar.classList.add('hidden');
    this.clearHighlights('source');
    this.clearHighlights('preview');
    // Hand focus back to the pane so Esc feels like "dismiss and continue"
    if (this.scope === 'source' && this.isPaneVisible('source')) {
      this.opts.getEditorView()?.focus();
    }
  }

  next(): void {
    this.step(1);
  }

  prev(): void {
    this.step(-1);
  }

  /** Recompute matches for the current scope (e.g. after content changed). */
  refresh(): void {
    if (!this.openState) return;
    this.runSearch({ resetCurrent: false, scroll: false });
  }

  /**
   * CodeMirror extensions: the highlight field plus high-precedence key
   * bindings so Mod-f inside the editor opens THIS search (not the
   * basicSetup search panel).
   */
  editorExtensions(): Extension[] {
    const nav = (backwards: boolean) => (): boolean => {
      if (!this.openState) {
        this.open('source');
      } else if (backwards) {
        this.prev();
      } else {
        this.next();
      }
      return true;
    };
    return [
      searchHighlightField,
      Prec.highest(keymap.of([
        { key: 'Mod-f', run: () => { this.open('source'); return true; } },
        { key: 'Mod-g', run: nav(false) },
        { key: 'Shift-Mod-g', run: nav(true) },
        { key: 'F3', run: nav(false) },
        { key: 'Shift-F3', run: nav(true) },
      ])),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && this.openState && this.scope === 'source') {
          this.scheduleRefresh();
        }
      }),
    ];
  }

  // -------------------------------------------------------------------------
  // Find bar UI
  // -------------------------------------------------------------------------

  private buildBar(): void {
    this.bar = document.createElement('div');
    this.bar.className = 'pane-search hidden';
    this.bar.setAttribute('role', 'search');
    this.bar.innerHTML = `
      <div class="pane-search-scope" role="group" aria-label="Search scope">
        <button type="button" data-scope="source" title="Search the source editor">Source</button>
        <button type="button" data-scope="preview" title="Search the rendered documentation">Preview</button>
      </div>
      <input class="pane-search-input" type="text" placeholder="Find…" spellcheck="false" autocomplete="off" aria-label="Find">
      <button type="button" class="pane-search-btn pane-search-case" title="Match case">Aa</button>
      <span class="pane-search-count" aria-live="polite"></span>
      <button type="button" class="pane-search-btn pane-search-prev" title="Previous match (Shift+Enter)">
        <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 -960 960 960" fill="currentColor"><path d="M480-554 283-357l-57-57 254-254 254 254-57 57-197-197Z"/></svg>
      </button>
      <button type="button" class="pane-search-btn pane-search-next" title="Next match (Enter)">
        <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 -960 960 960" fill="currentColor"><path d="M480-360 226-614l57-57 197 197 197-197 57 57-254 254Z"/></svg>
      </button>
      <button type="button" class="pane-search-btn pane-search-close" title="Close (Esc)">&times;</button>
    `;

    this.input = this.bar.querySelector('.pane-search-input') as HTMLInputElement;
    this.countEl = this.bar.querySelector('.pane-search-count') as HTMLSpanElement;
    this.caseBtn = this.bar.querySelector('.pane-search-case') as HTMLButtonElement;
    this.prevBtn = this.bar.querySelector('.pane-search-prev') as HTMLButtonElement;
    this.nextBtn = this.bar.querySelector('.pane-search-next') as HTMLButtonElement;
    this.scopeGroup = this.bar.querySelector('.pane-search-scope') as HTMLDivElement;
    for (const btn of Array.from(this.scopeGroup.querySelectorAll('button'))) {
      this.scopeButtons[btn.dataset.scope as SearchScope] = btn as HTMLButtonElement;
    }

    // Keep focus in the input when clicking bar controls
    for (const btn of Array.from(this.bar.querySelectorAll('button'))) {
      btn.addEventListener('mousedown', (e) => e.preventDefault());
    }

    this.scopeButtons.source.addEventListener('click', () => this.setScope('source'));
    this.scopeButtons.preview.addEventListener('click', () => this.setScope('preview'));

    this.caseBtn.addEventListener('click', () => {
      this.caseSensitive = !this.caseSensitive;
      this.caseBtn.classList.toggle('active', this.caseSensitive);
      this.runSearch({ resetCurrent: true, scroll: true });
    });

    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());
    (this.bar.querySelector('.pane-search-close') as HTMLButtonElement)
      .addEventListener('click', () => this.close());

    this.input.addEventListener('input', () => {
      this.runSearch({ resetCurrent: true, scroll: true });
    });

    this.input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) this.prev(); else this.next();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); // don't let the app-level Escape close modals too
        this.close();
      } else if ((e.metaKey || e.ctrlKey) && e.code === 'KeyF') {
        e.preventDefault(); // already open: just reselect the query
        this.input.select();
      }
    });
  }

  private attachBar(): void {
    const panel = this.scope === 'source' ? this.opts.sourcePanel : this.opts.previewPanel;
    if (this.bar.parentElement !== panel) panel.appendChild(this.bar);
  }

  private updateScopeUI(): void {
    const sourceAvail = this.isPaneVisible('source');
    const previewAvail = this.isPaneVisible('preview');
    // With a single visible pane there is nothing to switch — hide the toggle
    this.scopeGroup.classList.toggle('hidden', !(sourceAvail && previewAvail));
    this.scopeButtons.source.classList.toggle('active', this.scope === 'source');
    this.scopeButtons.preview.classList.toggle('active', this.scope === 'preview');
  }

  private setScope(scope: SearchScope): void {
    if (scope === this.scope || !this.isPaneVisible(scope)) return;
    this.clearHighlights(this.scope);
    this.scope = scope;
    this.attachBar();
    this.updateScopeUI();
    this.input.focus(); // re-parenting the bar drops focus
    this.runSearch({ resetCurrent: true, scroll: true });
  }

  private isPaneVisible(scope: SearchScope): boolean {
    const panel = scope === 'source' ? this.opts.sourcePanel : this.opts.previewPanel;
    return !panel.classList.contains('hidden');
  }

  private selectedTextInScope(): string | null {
    let text = '';
    if (this.scope === 'source') {
      const view = this.opts.getEditorView();
      if (!view) return null;
      const sel = view.state.selection.main;
      text = view.state.sliceDoc(sel.from, sel.to);
    } else {
      text = window.getSelection()?.toString() ?? '';
    }
    if (!text || text.length > 200 || text.includes('\n')) return null;
    return text;
  }

  private updateCount(total: number): void {
    const query = this.input.value;
    if (!query) {
      this.countEl.textContent = '';
      this.countEl.classList.remove('no-results');
    } else if (total === 0) {
      this.countEl.textContent = 'No results';
      this.countEl.classList.add('no-results');
    } else {
      const totalLabel = this.capped ? `${total}+` : String(total);
      this.countEl.textContent = `${this.currentIndex + 1} of ${totalLabel}`;
      this.countEl.classList.remove('no-results');
    }
    this.prevBtn.disabled = total === 0;
    this.nextBtn.disabled = total === 0;
  }

  // -------------------------------------------------------------------------
  // Search core
  // -------------------------------------------------------------------------

  private buildRegex(query: string): RegExp | null {
    if (!query) return null;
    return new RegExp(escapeRegExp(query), this.caseSensitive ? 'g' : 'gi');
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => {
      this.refreshTimer = null;
      this.refresh();
    }, 200);
  }

  private step(direction: 1 | -1): void {
    const total = this.scope === 'source' ? this.editorMatches.length : this.previewRanges.length;
    if (total === 0) return;
    this.currentIndex = (this.currentIndex + direction + total) % total;
    if (this.scope === 'source') {
      this.applyEditorHighlights(true);
    } else {
      this.applyPreviewHighlights();
      this.scrollPreviewToCurrent();
    }
    this.updateCount(total);
  }

  private runSearch(opts: { resetCurrent: boolean; scroll: boolean }): void {
    if (this.scope === 'source') {
      this.searchEditor(opts);
    } else {
      this.searchPreview(opts);
    }
  }

  // ----- Source (CodeMirror) driver ----------------------------------------

  private searchEditor(opts: { resetCurrent: boolean; scroll: boolean }): void {
    const view = this.opts.getEditorView();
    this.editorMatches = [];
    this.capped = false;
    if (view) {
      const regex = this.buildRegex(this.input.value);
      if (regex) {
        const text = view.state.doc.toString();
        let m: RegExpExecArray | null;
        while ((m = regex.exec(text)) !== null) {
          if (m[0].length === 0) { regex.lastIndex++; continue; }
          this.editorMatches.push({ from: m.index, to: m.index + m[0].length });
          if (this.editorMatches.length >= MAX_MATCHES) { this.capped = true; break; }
        }
      }
    }

    if (opts.resetCurrent) {
      // Start from the match at/after the cursor, like most editors
      const head = view?.state.selection.main.head ?? 0;
      const idx = this.editorMatches.findIndex((r) => r.from >= head);
      this.currentIndex = idx === -1 ? 0 : idx;
    } else {
      this.currentIndex = Math.min(this.currentIndex, Math.max(0, this.editorMatches.length - 1));
    }

    this.applyEditorHighlights(opts.scroll && this.editorMatches.length > 0);
    this.updateCount(this.editorMatches.length);
  }

  private applyEditorHighlights(scrollToCurrent: boolean): void {
    const view = this.opts.getEditorView();
    if (!view) return;
    const spec = this.editorMatches.length
      ? { ranges: this.editorMatches, current: this.currentIndex }
      : null;
    const effects: StateEffect<unknown>[] = [setEditorMatches.of(spec)];
    const current = this.editorMatches[this.currentIndex];
    if (scrollToCurrent && current) {
      effects.push(EditorView.scrollIntoView(current.from, { y: 'center' }));
      view.dispatch({ selection: { anchor: current.from, head: current.to }, effects });
    } else {
      view.dispatch({ effects });
    }
  }

  // ----- Preview (DOM) driver -----------------------------------------------

  private searchPreview(opts: { resetCurrent: boolean; scroll: boolean }): void {
    this.previewRanges = [];
    this.capped = false;

    // Whitespace runs collapse to a single space in both haystack and query,
    // so "foo bar" matches even when the HTML source wraps lines. Queries
    // never contain '\n', so the '\n' block separator prevents matches from
    // bleeding across paragraphs/list items/table cells.
    const query = this.input.value.replace(/\s+/g, ' ');
    const regex = this.buildRegex(query);
    if (regex) {
      const { haystack, positions } = this.buildPreviewIndex();
      let m: RegExpExecArray | null;
      while ((m = regex.exec(haystack)) !== null) {
        if (m[0].length === 0) { regex.lastIndex++; continue; }
        const start = positions[m.index];
        const end = positions[m.index + m[0].length - 1];
        if (start && end) {
          const range = document.createRange();
          range.setStart(start.node, start.offset);
          range.setEnd(end.node, end.offset + 1);
          this.previewRanges.push(range);
          if (this.previewRanges.length >= MAX_MATCHES) { this.capped = true; break; }
        }
      }
    }

    if (opts.resetCurrent) {
      this.currentIndex = this.firstPreviewMatchInView();
    } else {
      this.currentIndex = Math.min(this.currentIndex, Math.max(0, this.previewRanges.length - 1));
    }

    this.applyPreviewHighlights();
    if (opts.scroll && this.previewRanges.length > 0) this.scrollPreviewToCurrent();
    this.updateCount(this.previewRanges.length);
  }

  private buildPreviewIndex(): { haystack: string; positions: (CharPosition | null)[] } {
    const container = this.opts.previewContainer;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = (node as globalThis.Text).parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest(CHROME_SELECTOR)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const blockCache = new Map<Element, Element>();
    const blockOf = (el: Element): Element => {
      const cached = blockCache.get(el);
      if (cached) return cached;
      let cur: Element | null = el;
      while (cur && cur !== container && !BLOCK_TAGS.has(cur.tagName)) {
        cur = cur.parentElement;
      }
      const block = cur ?? container;
      blockCache.set(el, block);
      return block;
    };

    let haystack = '';
    const positions: (CharPosition | null)[] = [];
    let lastWasSpace = true;
    let lastBlock: Element | null = null;

    let node: Node | null;
    while ((node = walker.nextNode())) {
      const textNode = node as globalThis.Text;
      const block = blockOf(textNode.parentElement as Element);
      if (lastBlock && block !== lastBlock && !lastWasSpace) {
        haystack += '\n';
        positions.push(null);
        lastWasSpace = true;
      }
      lastBlock = block;

      const text = textNode.data;
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (/\s/.test(ch)) {
          if (!lastWasSpace) {
            haystack += ' ';
            positions.push({ node: textNode, offset: i });
            lastWasSpace = true;
          }
        } else {
          haystack += ch;
          positions.push({ node: textNode, offset: i });
          lastWasSpace = false;
        }
      }
    }

    return { haystack, positions };
  }

  private firstPreviewMatchInView(): number {
    const containerTop = this.opts.previewContainer.getBoundingClientRect().top;
    const limit = Math.min(this.previewRanges.length, 1000);
    for (let i = 0; i < limit; i++) {
      if (this.previewRanges[i].getBoundingClientRect().bottom >= containerTop) return i;
    }
    return 0;
  }

  private applyPreviewHighlights(): void {
    const registry = highlightRegistry();
    if (!registry) return; // no Highlight API: navigation still works via scrolling + selection
    registry.delete(HIGHLIGHT_ALL);
    registry.delete(HIGHLIGHT_CURRENT);
    if (this.previewRanges.length === 0) return;
    const HighlightCtor = (globalThis as any).Highlight;
    const others = this.previewRanges.filter((_, i) => i !== this.currentIndex);
    if (others.length) registry.set(HIGHLIGHT_ALL, new HighlightCtor(...others));
    const current = this.previewRanges[this.currentIndex];
    if (current) registry.set(HIGHLIGHT_CURRENT, new HighlightCtor(current));
  }

  private scrollPreviewToCurrent(): void {
    const range = this.previewRanges[this.currentIndex];
    if (!range) return;

    // Expand a collapsed code block so the match is actually visible
    const startEl = range.startContainer instanceof Element
      ? range.startContainer
      : range.startContainer.parentElement;
    const collapsed = startEl?.closest('.code-collapse-wrapper.collapsed');
    if (collapsed) {
      (collapsed.querySelector('.code-collapse-toggle') as HTMLElement | null)?.click();
    }

    // Fallback visibility when the Highlight API is unavailable
    if (!highlightRegistry()) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range.cloneRange());
    }

    const container = this.opts.previewContainer;
    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    container.scrollTop += rect.top - containerRect.top - (container.clientHeight / 2 - rect.height / 2);
  }

  // -------------------------------------------------------------------------

  private clearHighlights(scope: SearchScope): void {
    if (scope === 'source') {
      this.editorMatches = [];
      this.opts.getEditorView()?.dispatch({ effects: setEditorMatches.of(null) });
    } else {
      this.previewRanges = [];
      const registry = highlightRegistry();
      registry?.delete(HIGHLIGHT_ALL);
      registry?.delete(HIGHLIGHT_CURRENT);
    }
  }
}
